import repositories from '@/repositories'
import { cloneDeep } from 'lodash'

export const useSurveys = () => {
  const getSurveys = async (params:any) => {
    try {
      const res = await repositories.surveys.get(params)
      return {
        data: res.surveys,
        totalCount: res.meta.totalCount,
        totalPages: res.meta.totalPages
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }
  const postSurvey = async (surveyObj: any, params:any) => {
    try {
      const res = await repositories.surveys.post(surveyObj, params)
      return {
        data: res['survey/survey']
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const patchSurvey = async (id: string | number, surveyObj: any, params:any) => {
    try {
      const res = await repositories.surveys.patch(id, surveyObj, params)
      return {
        data: res['survey/surveys']
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  // Map observation in survey
  const addAnswersToSurvey = (survey: any, observations: any) => {
    observations.forEach((observation: any) => {
      const question = survey.questions.find((q: any) => q.id === observation.questionId)
      if (question) {
        if (observation.observationDocuments?.length > 0) {
          question.defaultAnswer = observation.observationDocuments
          // Added documentId to update posted document only
          question.documentId = observation.observationDocuments[0].id
        } else if (observation.answerChoices.length) {
          question.defaultAnswer = observation.answerChoices.map((answer: any) => { return answer.id })
        } else if (observation?.answerToDate) {
          // answerToDate we are using for date/time picker which is coming in UTC
          question.defaultAnswer = observation.answerToDate
        } else {
          if (question.questionText === 'Address' && survey.name === 'General Information' && observation.answer) {
            question.defaultAnswer = JSON.parse(observation.answer)?.formattedAddress || observation.answer
          } else if (['Aggregate', 'Per Occurrence'].includes(question.questionText) && observation.answer) {
            question.defaultAnswer = formattedValue(observation.answer) || observation.answer
          } else if (observation.observationPhotos?.length > 0) {
            question.defaultAnswer = observation.observationPhotos
            question.documentId = observation.observationPhotos[0].id
          } else if (observation.formType === 'Photos' && !observation.observationPhotos?.length) {
            question.defaultAnswer = []
          } else if (observation.answerChoice) {
            question.defaultAnswer = observation.answerChoice
          } else {
            question.defaultAnswer = observation.answer
          }
        }
        question.observationId = observation.id
      }
    })
    return survey
  }

  // Map public observation in local observations
  const mapObservationsToLocalObservations = (localObservation: any, publicObservations: any) => {
    const newObservations = [] as any
    if (localObservation.length) {
      // Need to replace observation ID
      publicObservations.forEach((observation: any) => {
        const question = localObservation.find((b: any) => b.questionId === observation.questionId)
        if (question) {
          const newObj = { ...observation }
          newObj.id = question.id
          newObj.surveyId = question.surveyId
          // Need to keep local document observation Id
          if (question.observationDocuments[0]?.id) {
            newObj.observationDocuments[0].id = question.observationDocuments[0].id
          }
          newObservations.push(newObj)
        }
      })
    } else {
      // there on no local document
      // Need to remove observation ID
      publicObservations.forEach((observation: any) => {
        const newObj = { ...observation }
        newObj.id = null
        newObj.surveyId = null
        newObservations.push(newObj)
      })
    }
    return newObservations
  }

  const reviewSurvey = async (surveyId: number) => {
    try {
      const res = await repositories.surveys.reviewSurvey(surveyId)
      return {
        data: res
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const formattedValue = (num: string) => {
    const inputValue = num.replace(/([a-zA-Z , . $])/g, '')
    const formatted = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(inputValue))
    return formatted.slice(0, formatted.length - 3)
  }

  const getSurveyReportsByResource = async (params:any) => {
    try {
      const res = await repositories.surveys.getSurveyReportsByResource(params)
      return {
        data: res.surveyReports,
        totalCount: res.meta.totalCount,
        totalPages: res.meta.totalPages
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const getSurveyReportById = async (params: any) => {
    try {
      const res = await repositories.surveys.getSurveyReportById(params)
      return {
        data: res?.['survey/survey']
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const getSurveyReportsByAssetId = async (params: any) => {
    try {
      const res = await repositories.surveys.getSurveyReportsByAssetId(params)
      return {
        data: res.surveyReports,
        totalCount: res.meta.totalCount,
        totalPages: res.meta.totalPages
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const getSurveyReportsByEquipmentReportId = async (params: any) => {
    try {
      const res = await repositories.surveys.getSurveyReportsByEquipmentReportId(params)
      return {
        data: res.equipmentReport
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const getSurveyReportsByResourceID = async (params: any) => {
    try {
      const res = await repositories.surveys.getSurveyReportsByResourceID(params)
      return {
        data: res.surveyReports,
        totalCount: res.meta.totalCount,
        totalPages: res.meta.totalPages
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const getDeficiencyReportByResourceID = async (params: any) => {
    try {
      const res = await repositories.surveys.getDeficiencyReportByResourceID(params)
      return {
        data: res.deficiencyReport
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  const getRepairReportByResourceID = async (params: any) => {
    try {
      const res = await repositories.surveys.getRepairReportByResourceID(params)
      return {
        data: res.repairReport
      }
    } catch (err) {
      console.log(err)
      return { data: false }
    }
  }

  return {
    getSurveys,
    postSurvey,
    patchSurvey,
    addAnswersToSurvey,
    reviewSurvey,
    mapObservationsToLocalObservations,
    getSurveyReportsByResource,
    getSurveyReportById,
    getSurveyReportsByAssetId,
    getSurveyReportsByEquipmentReportId,
    getSurveyReportsByResourceID,
    getDeficiencyReportByResourceID,
    getRepairReportByResourceID
  }
}
